import React from 'react';
import fr from 'facet-rest';
import PropTypes from 'prop-types';

import useUserContext from 'Context/UserContext';
import useComponentDidMount from 'Hooks/useComponentDidMount';

const OnboardingContext = React.createContext(undefined);

// Actions
export const INITIALIZE_STARTED = 'initialize-started';
export const INITIALIZE_SUCCEEDED = 'initialize-succeeded';
export const INITIALIZE_FAILED = 'initialize-failed';

export const CLIENT_REFRESH_STARTED = 'client-refresh-started';
export const CLIENT_REFRESH_SUCCEEDED = 'client-refresh-succeeded';
export const CLIENT_REFRESH_FAILED = 'client-refresh-failed';

export const HOUSEHOLD_REFRESH_STARTED = 'household-refresh-started';
export const HOUSEHOLD_REFRESH_SUCCEEDED = 'household-refresh-succeeded';
export const HOUSEHOLD_REFRESH_FAILED = 'household-refresh-failed';

// Initialize State
const init = () => {
  return {
    isInitializing: true,
    initializingError: null,
    clientResource: null,
    isClientRefreshing: false,
    clientRefreshError: null,
    householdResource: null,
    isHouseholdRefreshing: false,
    householdRefreshError: null,
  };
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case INITIALIZE_STARTED: {
      return {
        ...state,
        isInitializing: true,
        initializingError: null,
      };
    }
    case INITIALIZE_SUCCEEDED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: null,
        clientResource: action.clientResource,
        householdResource: action.householdResource,
      };
    }
    case INITIALIZE_FAILED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: action.initializationError,
      };
    }
    case CLIENT_REFRESH_STARTED: {
      return {
        ...state,
        isClientRefreshing: true,
        clientRefreshError: null,
      };
    }
    case CLIENT_REFRESH_SUCCEEDED: {
      return {
        ...state,
        isClientRefreshing: false,
        clientRefreshError: null,
        clientResource: action.clientResource,
      };
    }
    case CLIENT_REFRESH_FAILED: {
      return {
        ...state,
        isClientRefreshing: false,
        clientRefreshError: action.clientRefreshError,
      };
    }
    case HOUSEHOLD_REFRESH_STARTED: {
      return {
        ...state,
        isHouseholdRefreshing: true,
        householdRefreshError: null,
      };
    }
    case HOUSEHOLD_REFRESH_SUCCEEDED: {
      return {
        ...state,
        isHouseholdRefreshing: false,
        householdRefreshError: null,
        householdResource: action.householdResource,
      };
    }
    case HOUSEHOLD_REFRESH_FAILED: {
      return {
        ...state,
        isHouseholdRefreshing: false,
        householdRefreshError: action.householdRefreshError,
      };
    }
    default: {
      throw new Error(`Unhandled onboarding-context action type: ${action.type}`);
    }
  }
};

const OnboardingProvider = ({
  children,
}) => {
  const { userResource } = useUserContext();
  const [state, dispatch] = React.useReducer(reducer, null, init);

  const refreshClientResource = React.useCallback(async () => {
    try {
      dispatch({ type: CLIENT_REFRESH_STARTED });
      const [clientRes] = await userResource.relationship(fr.relationships.client.name).link('related').get();
      dispatch({ type: CLIENT_REFRESH_SUCCEEDED, clientResource: clientRes });
    } catch (err) {
      dispatch({ type: CLIENT_REFRESH_FAILED, clientRefreshError: err });
    }
  }, [userResource]);

  const refreshHouseholdResource = React.useCallback(async () => {
    try {
      dispatch({ type: HOUSEHOLD_REFRESH_STARTED });
      const [householdRes] = await userResource.relationship(fr.relationships.household.name).link('related').get();
      dispatch({ type: HOUSEHOLD_REFRESH_SUCCEEDED, householdResource: householdRes });
    } catch (err) {
      dispatch({ type: HOUSEHOLD_REFRESH_FAILED, householdRefreshError: err });
    }
  }, [userResource]);

  const memoizedValue = React.useMemo(() => {
    return {
      isInitializing: state.isInitializing,
      initializingError: state.initializingError,
      clientResource: state.clientResource,
      isClientRefreshing: state.isClientRefreshing,
      clientRefreshError: state.clientRefreshError,
      householdResource: state.householdResource,
      isHouseholdRefreshing: state.isHouseholdRefreshing,
      householdRefreshError: state.householdRefreshError,
      refreshClientResource,
      refreshHouseholdResource,
    };
  }, [state, refreshClientResource, refreshHouseholdResource]);

  useComponentDidMount(async () => {
    try {
      dispatch({ type: INITIALIZE_STARTED });
      const [clientResp, householdResp] = await Promise.all([
        userResource.relationship(fr.relationships.client.name).link('related').get(),
        userResource.relationship(fr.relationships.household.name).link('related').get(),
      ]);

      const [clientRes] = clientResp;
      const [householdRes] = householdResp;

      dispatch({
        type: INITIALIZE_SUCCEEDED,
        clientResource: clientRes,
        householdResource: householdRes,
      });
    } catch (err) {
      dispatch({ type: INITIALIZE_FAILED, initializationError: err });
    }
  });

  return (
    <OnboardingContext.Provider value={memoizedValue}>
      {children}
    </OnboardingContext.Provider>
  );
};

const useOnboardingContext = () => {
  const context = React.useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error('useOnboardingContext must be used within a OnboardingProvider');
  }
  return context;
};

OnboardingProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

export {
  OnboardingProvider,
  useOnboardingContext,
};
